/**
 * tokenStorage.js
 * ──────────────────────────────────────────────────────────────────────────
 * Utilitaire centralisé pour gérer le stockage du token d'authentification.
 *
 * Principe "Se souvenir de moi" :
 *   - rememberMe = true  → localStorage  (persiste après fermeture du navigateur)
 *   - rememberMe = false → sessionStorage (effacé à la fermeture de l'onglet)
 *
 * La lecture est transparente : on cherche d'abord dans sessionStorage
 * (session active), puis dans localStorage (session persistante).
 * ──────────────────────────────────────────────────────────────────────────
 */

const SESSION_KEYS = ['token', 'loggedIn', 'email', 'uid'];

// ─────────────────────────────────────────────
// ÉCRITURE
// ─────────────────────────────────────────────

/**
 * Sauvegarde les données de session dans le bon stockage selon rememberMe.
 * @param {{ token: string, email: string, uid: string }} data
 * @param {boolean} rememberMe
 */
export function saveSession(data, rememberMe) {
    const storage = rememberMe ? localStorage : sessionStorage;

    if (data.token) {
        storage.setItem('token', data.token);
        storage.setItem('loggedIn', 'true');
    }
    if (data.email)  storage.setItem('email', data.email);
    if (data.uid)    storage.setItem('uid', String(data.uid));

    // Mémoriser où on a stocké pour le retrouver au rechargement
    if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
    } else {
        localStorage.removeItem('rememberMe');
    }
}

// ─────────────────────────────────────────────
// LECTURE TRANSPARENTE
// ─────────────────────────────────────────────

/**
 * Lit une clé depuis sessionStorage en premier, puis localStorage.
 * Cela couvre les deux cas (session normale + "Se souvenir de moi").
 * @param {string} key
 * @returns {string|null}
 */
export function getSessionItem(key) {
    return sessionStorage.getItem(key) ?? localStorage.getItem(key);
}

/**
 * Retourne le token JWT, qu'il soit dans sessionStorage ou localStorage.
 * @returns {string|null}
 */
export function getToken() {
    return getSessionItem('token');
}

/**
 * Retourne true si une session active existe (dans l'un ou l'autre stockage).
 * @returns {boolean}
 */
export function isSessionActive() {
    return !!(sessionStorage.getItem('token') || localStorage.getItem('token'));
}

/**
 * Retourne true si le mode "Se souvenir de moi" était actif lors de la dernière connexion.
 * @returns {boolean}
 */
export function wasRemembered() {
    return localStorage.getItem('rememberMe') === 'true';
}

// ─────────────────────────────────────────────
// RESTAURATION (au démarrage de l'app)
// ─────────────────────────────────────────────

/**
 * Au rechargement de l'app, si un token persiste dans localStorage
 * (mode "Se souvenir de moi"), on le copie dans sessionStorage pour que
 * tous les getItem('token') existants fonctionnent sans modification.
 */
export function restoreSessionFromStorage() {
    const persistedToken = localStorage.getItem('token');
    if (persistedToken && !sessionStorage.getItem('token')) {
        // Recopier les clés dans sessionStorage pour compatibilité avec le code existant
        SESSION_KEYS.forEach(key => {
            const value = localStorage.getItem(key);
            if (value) sessionStorage.setItem(key, value);
        });
    }
}

// ─────────────────────────────────────────────
// NETTOYAGE (logout)
// ─────────────────────────────────────────────

/**
 * Efface toutes les données de session des deux stockages.
 */
export function clearSession() {
    SESSION_KEYS.forEach(key => {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
    });
    localStorage.removeItem('rememberMe');
}
